import React from 'react';
import { Link } from 'react-router-dom';
import { Footer } from '@/modules/landing';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-zapt-navy relative overflow-hidden">
      {/* Background Image */}
      <div 
        className="absolute inset-0 bg-cover bg-center bg-no-repeat opacity-20"
        style={{ backgroundImage: 'url(https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/background_image.jpg)' }}
      />
      
      {/* Gradient overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-zapt-navy/80 via-zapt-navy/90 to-zapt-navy/80" />

      {/* Navigation */}
      <nav className="border-b border-white/10 bg-zapt-navy/95 backdrop-blur-md relative z-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <img 
                src="https://zapt-cdn.com/dc408fb4-e206-4be6-8677-dfc0f17d99f7/Zapt_White%2BLogo%2B(1).png" 
                alt="ZAPT" 
                className="h-10 w-auto"
              />
            </Link>
            <Link to="/" className="text-gray-400 hover:text-white transition-colors font-medium">
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 relative z-10">
        <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-10 shadow-xl">
          <h1 className="text-3xl sm:text-4xl font-black text-white mb-2">Terms of Service</h1>
          <p className="text-gray-400 mb-8">Last updated: January 2025</p>

          <div className="prose prose-invert prose-lg max-w-none">
            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">1. Agreement to Terms</h2>
              <p className="text-gray-300 mb-4">
                By accessing or using ZAPT ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of these terms, you may not access the Service.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">2. Description of Service</h2>
              <p className="text-gray-300 mb-4">
                ZAPT is a platform that builds custom workflow apps for your business in 2-5 days. The Service includes:
              </p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li>Custom workflow app development and delivery</li>
                <li>Hosting and maintenance of apps we build</li>
                <li>Platform access for requesting and managing app builds</li>
                <li>Ongoing support and updates for apps we build</li>
                <li>Project management and communication throughout the build process</li>
              </ul>
              <p className="text-gray-300 mb-4">
                We build apps that automate manual processes, optimize workflows, and help your business scale. We handle everything — building, hosting, and maintenance — so you can focus on using the apps.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">3. User Accounts</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">3.1 Account Creation</h3>
              <p className="text-gray-300 mb-4">
                To use the Service, you must create an account by providing a valid email address. You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">3.2 Account Responsibilities</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li>You must be at least 18 years old to use this Service</li>
                <li>You must provide accurate and complete information</li>
                <li>You must not share your account with others</li>
                <li>You must notify us immediately of any unauthorized access</li>
                <li>You are responsible for all activities under your account</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">4. Build Requests and Payment</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">4.1 Build Requests</h3>
              <p className="text-gray-300 mb-4">
                You may submit requests for custom workflow apps through our platform. Each build request will be evaluated, and we will provide a quote and timeline. Build requests are typically completed within 2-5 days, though complex projects may take longer.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">4.2 Pricing and Billing</h3>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li>Pricing is determined on a per-project basis and will be communicated before work begins</li>
                <li>Payment may be required in installments (e.g., deposit and final payment) as specified in your build request</li>
                <li>Payment processing is handled securely by Stripe</li>
                <li>Prices are quoted in advance and will not change once a build request is approved</li>
                <li>Ongoing hosting and maintenance fees may apply as specified in your agreement</li>
              </ul>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">4.3 Refunds and Cancellation</h3>
              <p className="text-gray-300 mb-4">
                Refund policies will be specified in your individual build request agreement. Generally, deposits may be non-refundable once work has begun. You may cancel a build request before work begins, subject to any cancellation fees specified in your agreement.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">4.4 Ongoing Maintenance</h3>
              <p className="text-gray-300 mb-4">
                We provide hosting and maintenance for apps we build. Ongoing maintenance fees, if applicable, will be specified in your build request agreement. We reserve the right to modify maintenance fees with reasonable notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">5. Acceptable Use Policy</h2>
              <p className="text-gray-300 mb-3">You agree not to:</p>
              <ul className="list-disc pl-6 mb-4 text-gray-300 space-y-2">
                <li>Use the Service for any illegal or unauthorized purpose</li>
                <li>Violate any laws or regulations</li>
                <li>Infringe on intellectual property rights</li>
                <li>Transmit malicious code, viruses, or harmful content</li>
                <li>Attempt to gain unauthorized access to the Service</li>
                <li>Interfere with or disrupt the Service</li>
                <li>Use the Service to harass, abuse, or harm others</li>
                <li>Scrape, copy, or reverse engineer the Service</li>
                <li>Share or resell the Service without authorization</li>
                <li>Circumvent usage limitations or billing mechanisms</li>
              </ul>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">6. Intellectual Property</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">6.1 Custom Apps We Build</h3>
              <p className="text-gray-300 mb-4">
                Upon full payment for a custom app, you own the app we build for you. You have the right to use, modify, and distribute the app as you see fit. We retain the right to use the app's code and design patterns for our own internal purposes and to build similar apps for other clients, but we will not share your specific business logic or data.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">6.2 Platform and Service</h3>
              <p className="text-gray-300 mb-4">
                The ZAPT platform, including its design, features, and functionality, is owned by ZAPT and protected by copyright, trademark, and other intellectual property laws. You may not copy, modify, or distribute any part of the platform without our written permission.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">6.3 Your Data and Content</h3>
              <p className="text-gray-300 mb-4">
                You retain all rights to your data, business processes, and requirements that you share with us. By using the Service, you grant us a limited license to use this information solely for the purpose of building your custom apps.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">7. Build Process and Deliverables</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">7.1 Build Timeline</h3>
              <p className="text-gray-300 mb-4">
                We aim to complete custom apps within 2-5 days, though timelines may vary based on complexity. We will communicate any delays and provide updated timelines as needed.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">7.2 Requirements and Changes</h3>
              <p className="text-gray-300 mb-4">
                You are responsible for providing clear requirements for your custom app. Changes to requirements after work has begun may result in additional costs and timeline adjustments. We will communicate any impact of changes before proceeding.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">7.3 Acceptance and Testing</h3>
              <p className="text-gray-300 mb-4">
                Upon completion, you will have the opportunity to test and review the app. We will work with you to address any issues or requested changes within the scope of the original build request. Additional changes beyond the original scope may incur additional fees.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">8. Data and Privacy</h2>
              <p className="text-gray-300 mb-4">
                Your use of the Service is also governed by our Privacy Policy. We collect and process data as described in our Privacy Policy, which is incorporated into these Terms by reference.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">9. Service Availability</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">9.1 Service Level</h3>
              <p className="text-gray-300 mb-4">
                We strive to provide reliable service but do not guarantee uninterrupted or error-free access. The Service is provided "as is" without warranties of any kind.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">9.2 Maintenance</h3>
              <p className="text-gray-300 mb-4">
                We may perform scheduled or emergency maintenance that temporarily interrupts the Service. We will provide reasonable notice when possible.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">9.3 Modifications</h3>
              <p className="text-gray-300 mb-4">
                We reserve the right to modify, suspend, or discontinue any part of the Service at any time with or without notice.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">10. Disclaimers and Limitations of Liability</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">10.1 No Warranties</h3>
              <p className="text-gray-300 mb-4">
                THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">10.2 App Functionality</h3>
              <p className="text-gray-300 mb-4">
                While we strive to build apps that meet your requirements, we cannot guarantee that apps will be error-free or meet all possible use cases. You are responsible for testing apps and ensuring they meet your needs. We will work with you to address issues within the scope of the original build request.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">10.3 Limitation of Liability</h3>
              <p className="text-gray-300 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZAPT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, OR ANY LOSS OF PROFITS OR REVENUES, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, OR GOODWILL.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">10.4 Maximum Liability</h3>
              <p className="text-gray-300 mb-4">
                Our total liability to you for all claims arising from your use of the Service shall not exceed the amount you paid us in the 12 months preceding the claim.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">11. Indemnification</h2>
              <p className="text-gray-300 mb-4">
                You agree to indemnify and hold harmless ZAPT, its officers, directors, employees, and agents from any claims, losses, damages, liabilities, and expenses (including legal fees) arising from your use of the Service, violation of these Terms, or infringement of any rights of another.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">12. Termination</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">12.1 Termination by You</h3>
              <p className="text-gray-300 mb-4">
                You may terminate your account at any time by contacting us or through your account settings. Upon termination, your access to the platform will cease. Apps we have built for you will continue to function, subject to any ongoing hosting or maintenance agreements.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">12.2 Termination by Us</h3>
              <p className="text-gray-300 mb-4">
                We may suspend or terminate your account immediately if you violate these Terms, engage in fraudulent activity, or for any other reason at our sole discretion. We will provide notice when reasonably possible.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">12.3 Effect of Termination</h3>
              <p className="text-gray-300 mb-4">
                Upon termination, you will lose access to the platform, but apps we have built for you will continue to function subject to any ongoing hosting or maintenance agreements. Your data may be deleted in accordance with our data retention policies. Sections of these Terms that by their nature should survive termination will survive.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">13. Dispute Resolution</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">13.1 Informal Resolution</h3>
              <p className="text-gray-300 mb-4">
                Before filing a claim, you agree to contact us to attempt to resolve the dispute informally. We will attempt to resolve disputes in good faith.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">13.2 Governing Law</h3>
              <p className="text-gray-300 mb-4">
                These Terms shall be governed by and construed in accordance with the laws of England and Wales, without regard to conflict of law principles.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">13.3 Jurisdiction</h3>
              <p className="text-gray-300 mb-4">
                Any disputes not resolved informally shall be subject to the exclusive jurisdiction of the courts of England and Wales.
              </p>
            </section>

            <section className="mb-8">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">14. General Provisions</h2>
              
              <h3 className="text-lg font-semibold text-gray-200 mb-3">14.1 Entire Agreement</h3>
              <p className="text-gray-300 mb-4">
                These Terms, together with our Privacy Policy, constitute the entire agreement between you and ZAPT regarding the Service.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">14.2 Changes to Terms</h3>
              <p className="text-gray-300 mb-4">
                We may update these Terms from time to time. We will notify you of material changes by posting the new Terms on our website and updating the "Last updated" date. Your continued use of the Service after changes become effective constitutes acceptance of the new Terms.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">14.3 Severability</h3>
              <p className="text-gray-300 mb-4">
                If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full effect.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">14.4 Waiver</h3>
              <p className="text-gray-300 mb-4">
                Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
              </p>

              <h3 className="text-lg font-semibold text-gray-200 mb-3">14.5 Assignment</h3>
              <p className="text-gray-300 mb-4">
                You may not assign or transfer these Terms without our written consent. We may assign our rights and obligations without restriction.
              </p>
            </section>

            <section className="mb-0">
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-4">15. Contact Information</h2>
              <p className="text-gray-300 mb-4">
                If you have questions about these Terms, please contact us at:
              </p>
              <div className="bg-zapt-purple/20 border border-zapt-purple/30 rounded-xl p-6">
                <p className="text-white font-medium mb-2">ZAPT</p>
                <p className="text-gray-300">Email: info@zapt.ai</p>
              </div>
            </section>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
